import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
} from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./firebase";
import { logAuditEvent } from "./auditService";
import { ROLES } from "../config/roleConfig";

/**
 * Sign in with email and password
 */
export async function signIn(email, password) {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Update last login
    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, { lastLogin: serverTimestamp() });
    } catch (e) {
      // Profile might not exist yet if Firestore rules block or first legacy login
      console.warn("Could not update last login:", e);
    }

    await logAuditEvent("auth.login", { userId: user.uid, email: user.email });

    return { success: true, user };
  } catch (error) {
    console.error("Sign in error:", error);
    return { success: false, error: getAuthErrorMessage(error.code) };
  }
}

/**
 * Create a new user account
 * Supports all roles with role-specific profile fields
 *
 * @param {Object} params
 * @param {string} params.email
 * @param {string} params.password
 * @param {string} params.firstName
 * @param {string} params.lastName
 * @param {string} params.role - One of ROLES values (default: "agent")
 * @param {string} params.credentials - Professional credentials (e.g., "LCSW", "LPC")
 * @param {string} params.employeeId
 * @param {string} params.organization
 * @param {string} params.program
 * @param {string} [params.licenseType] - For clinician/prescriber (e.g., "LCSW", "MD", "APRN")
 * @param {string} [params.licenseNumber] - License number
 * @param {string} [params.licenseState] - State of licensure
 * @param {string} [params.specialty] - Clinical specialty
 * @param {string} [params.npiNumber] - National Provider Identifier
 * @param {string} [params.deaNumber] - DEA number (prescribers only)
 */
export async function signUp({
  email,
  password,
  firstName,
  lastName,
  role = ROLES.AGENT,
  credentials = "",
  employeeId = "",
  organization = "",
  program = "ICM",
  supervisorId = "",       // uid of direct supervisor
  // Clinician/prescriber-specific fields
  licenseType = "",
  licenseNumber = "",
  licenseState = "",
  specialty = "",
  npiNumber = "",
  deaNumber = "",
}) {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Set display name on Firebase Auth profile
    const displayName = `${firstName} ${lastName}`;
    await updateProfile(user, { displayName });

    // Base profile fields (all roles)
    const profileData = {
      email: user.email,
      displayName,
      firstName,
      lastName,
      credentials: credentials || "",
      employeeId: employeeId || "",
      role,
      organization: organization || "Highland Rivers Health",
      program: program || "",
      supervisorId: supervisorId || "",   // who this person reports to
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp(),
    };

    // Add clinician/prescriber-specific fields
    if (role === ROLES.CLINICIAN || role === ROLES.PRESCRIBER || role === ROLES.SUPERVISOR) {
      profileData.licenseType = licenseType;
      profileData.licenseNumber = licenseNumber;
      profileData.licenseState = licenseState;
      profileData.specialty = specialty;
      profileData.npiNumber = npiNumber;
    }

    // Prescriber-specific fields
    if (role === ROLES.PRESCRIBER) {
      profileData.deaNumber = deaNumber;
    }

    // Create user profile document in Firestore
    const userRef = doc(db, "users", user.uid);
    await setDoc(userRef, profileData);

    await logAuditEvent("auth.signup", {
      userId: user.uid,
      email: user.email,
      role,
    });

    return { success: true, user };
  } catch (error) {
    console.error("Sign up error:", error);
    return { success: false, error: getAuthErrorMessage(error.code) };
  }
}

/**
 * Update a user's profile fields (for admin/supervisor use)
 */
export async function updateUserProfile(uid, updates) {
  try {
    const userRef = doc(db, "users", uid);
    await updateDoc(userRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });

    await logAuditEvent("user.profile_update", {
      targetUserId: uid,
      fields: Object.keys(updates),
    });

    return { success: true };
  } catch (error) {
    console.error("Profile update error:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Sign out
 */
export async function signOut() {
  try {
    const userId = auth.currentUser?.uid;
    await firebaseSignOut(auth);
    if (userId) {
      await logAuditEvent("auth.logout", { userId });
    }
    return { success: true };
  } catch (error) {
    console.error("Sign out error:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Send password reset email
 */
export async function resetPassword(email) {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    console.error("Password reset error:", error);
    return { success: false, error: getAuthErrorMessage(error.code) };
  }
}

/**
 * Get current user's profile from Firestore
 */
export async function getUserProfile(uid) {
  try {
    const userRef = doc(db, "users", uid);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      const data = userSnap.data();
      // Sanitize role — Firestore text fields can carry invisible characters
      if (data.role) data.role = data.role.trim();
      return { id: userSnap.id, ...data };
    }
    return null;
  } catch (error) {
    console.error("Error fetching user profile:", error);
    return null;
  }
}

/**
 * Subscribe to auth state changes
 */
export function onAuthChange(callback) {
  return onAuthStateChanged(auth, callback);
}

/**
 * Convert Firebase auth error codes to user-friendly messages
 */
function getAuthErrorMessage(code) {
  switch (code) {
    case "auth/invalid-email":
      return "Invalid email address.";
    case "auth/user-disabled":
      return "This account has been disabled. Contact your administrator.";
    case "auth/user-not-found":
      return "No account found with this email.";
    case "auth/wrong-password":
      return "Incorrect password.";
    case "auth/invalid-credential":
      return "Invalid email or password.";
    case "auth/too-many-requests":
      return "Too many failed attempts. Please try again later.";
    case "auth/network-request-failed":
      return "Network error. Check your connection.";
    case "auth/email-already-in-use":
      return "An account with this email already exists.";
    case "auth/weak-password":
      return "Password must be at least 6 characters.";
    default:
      return "Authentication failed. Please try again.";
  }
}